import { useContext, useState } from "react";
import { CognitoContext } from "../../contexts/cognito/cognito-provider";
import {
  CognitoConfirmSignup,
  CognitoGetErrorMessage,
  CognitoResetPassword,
  ResendConfirmCode,
} from "../../library/cognito/confirm";
import { CurrentUserStorage } from "../../library/core/current-user";
import { GetEventInputValue, ValidateFormView } from "../form/core";
import { FormInput } from "../form/input";
import { isValidPassword, PASSWORD_HINT } from "../../library/core/utils";
import { PrimaryButton } from "../form/primary-button";
import { SecondaryButton } from "../form/secondary-button";
import { PasswordInput } from "../form/password-input";

const formView = {
  fields: [
    {
      name: "username",
      label: "Email address",
      type: "email",
      required: true,
    },
    {
      name: "verifyCode",
      label: "Verification code",
      type: "text",
      required: true,
    },
    {
      name: "password",
      label: "New Password",
      isPassword: true,
      type: "password",
      required: true,
      validate: (value) => {
        if (!isValidPassword(value)) return PASSWORD_HINT;
        return null;
      },
    },
    {
      name: "confirmPassword",
      label: "Confirm password",
      type: "password",
      required: true,
      isPassword: true,
      validate: (value, dataModel) => {
        if (value !== dataModel.password)
          return `this field does not match password!`;
        return null;
      },
    },
  ],
};

export const ResetPasswordForm = ({
  username,
  errorMessage,
  onSuccess,
  onCancel,
  onError,
}) => {
  username = username || CurrentUserStorage.getCurrentUser();
  const [model, setModel] = useState({ username });
  const [modelErrors, setModelErrors] = useState({});
  const cognitoContext = useContext(CognitoContext);

  const raiseError = (err) => {
    onError && onError(err);
    return false;
  };

  const validateForm = () => {
    if (!model.password || model.password.trim().length === 0) {
      return raiseError("password cat be empty");
    }

    if (!model.confirmPassword || model.confirmPassword.trim().length === 0) {
      return raiseError("password cat be empty");
    }

    if (model.password !== model.confirmPassword)
      return raiseError("passwords does not match");

    return true;
  };

  const onConfirmHandler = (event) => {
    event.preventDefault();

    const errs = ValidateFormView({ fields: formView.fields, model });
    setModelErrors(errs);

    if (!errs || Object.keys(errs).length === 0) {
      CognitoResetPassword({
        cognitoContext: cognitoContext.state,
        username: model.username,
        verifyCode: model.verifyCode,
        password: model.password,
        onSuccess: () => {
          onSuccess &&
            onSuccess({
              username: model.username,
              password: model.password,
            });
        },
        onError: (err) => {
          onError && onError(err);
        },
      });
    }
  };

  const resendCodeHandler = (event) => {
    event.preventDefault();
    ResendConfirmCode({
      cognitoContext: cognitoContext.state,
      username: model.username,
      onError,
    });
  };

  const onFieldChangeHandler = (event) => {
    setModel({
      ...model,
      [event.target.name]: GetEventInputValue(event),
    });
    setModelErrors({
      ...modelErrors,
      [event.target.name]: null,
    }); 
  };

  return (
    <form>
      <h1 className="text-center font-headings-primary font-medium text-[28px] leading-[33.6px] mb-4">
        Reset your password
      </h1>
      <div>
        <p className="text-center mb-4 text-[0.875rem] font-body-primary">
          If the email address you entered matches an existing account, please
          check your email for a verification code to reset your password.
        </p>
      </div>
    
      {formView.fields.map((field) =>
        field.isPassword ? (
          <PasswordInput
            key={field.name}
            name={field.name}
            value={model[field.name] || ""}
            onChange={onFieldChangeHandler}
            error={modelErrors?.[field.name]}
          />
        ) : (
          <FormInput
            key={field.name}
            dataModel={model}
            {...field}
            errorsModel={modelErrors}
            onChange={onFieldChangeHandler}
          />
        )
      )}

      <PrimaryButton onClick={onConfirmHandler}>Update Password</PrimaryButton>

      <SecondaryButton onClick={onCancel} className="mt-3 ">
        Cancel
      </SecondaryButton>
    </form>
  );
};
